<script lang="ts">
  import { t } from '$core/i18n';
  import {
    getMemoryWeightsForNode,
    getGlobalMemoryWeights,
    upsertMemoryWeights,
    deleteMemoryWeightsForNode,
    listNodes
  } from '$db/local/repositories';
  import type { SessionStore } from '$features/session';
  import { Modal } from '$ui/primitives/Modal';
  import { Button } from '$ui/primitives/Button';
  import { Checkbox } from '$ui/primitives/Checkbox';
  import { Icon } from '$ui/primitives/Icon';

  let {
    subjectId,
    subjectName,
    store,
    onClose
  }: {
    subjectId: string;
    subjectName: string;
    store: SessionStore;
    onClose: () => void;
  } = $props();

  const MIN_RETENTION = 0.70;
  const MAX_RETENTION = 0.97;
  const STEP_RETENTION = 0.01;
  const DEFAULT_RETENTION = 0.9;

  const MIN_INTERVAL = 7;
  const MAX_INTERVAL = 365;
  const STEP_INTERVAL = 7;
  const DEFAULT_INTERVAL = 365;

  interface SubtopicDraft {
    subtopicId: string;
    name: string;
    hasOverride: boolean;
    originalHasOverride: boolean;
    originalRetention: number | null;
    originalMaxInterval: number | null;
    retention: number;
    maxInterval: number;
  }

  let loading = $state(true);
  let busy = $state(false);
  let message = $state<string | null>(null);

  let subjectRetention = $state(DEFAULT_RETENTION);
  let subjectMaxInterval = $state(DEFAULT_INTERVAL);
  let initialSubjectRetention = $state(DEFAULT_RETENTION);
  let initialSubjectMaxInterval = $state(DEFAULT_INTERVAL);

  let subtopics = $state<SubtopicDraft[]>([]);
  let showSubtopics = $state(false);
  let showAdvanced = $state(false);

  let retentionLabel = $derived(`${Math.round(subjectRetention * 100)}%`);
  let intervalLabel = $derived(`${subjectMaxInterval}${t('subjectSettings.daysSuffix')}`);

  let dirty = $derived(computeDirty());

  function computeDirty(): boolean {
    if (Math.abs(subjectRetention - initialSubjectRetention) > 0.0001) return true;
    if (subjectMaxInterval !== initialSubjectMaxInterval) return true;
    for (const s of subtopics) {
      if (s.hasOverride !== s.originalHasOverride) return true;
      if (s.hasOverride) {
        if (
          s.originalRetention === null ||
          Math.abs(s.retention - s.originalRetention) > 0.0001
        ) {
          return true;
        }
        if (
          s.originalMaxInterval === null ||
          s.maxInterval !== s.originalMaxInterval
        ) {
          return true;
        }
      }
    }
    return false;
  }

  $effect(() => {
    void (async () => {
      const [subjectRow, globalRow] = await Promise.all([
        getMemoryWeightsForNode(subjectId),
        getGlobalMemoryWeights()
      ]);
      if (subjectRow) {
        subjectRetention = subjectRow.retentionTarget;
        subjectMaxInterval = subjectRow.maximumIntervalDays ?? DEFAULT_INTERVAL;
      } else {
        subjectRetention = globalRow?.retentionTarget ?? DEFAULT_RETENTION;
        subjectMaxInterval =
          globalRow?.maximumIntervalDays ?? DEFAULT_INTERVAL;
      }
      initialSubjectRetention = subjectRetention;
      initialSubjectMaxInterval = subjectMaxInterval;

      const allNodes = await listNodes();
      const list = allNodes.filter(n => n.parentId === subjectId);
      const drafts: SubtopicDraft[] = [];
      for (const st of list) {
        const overrideRow = await getMemoryWeightsForNode(st.id);
        if (overrideRow) {
          drafts.push({
            subtopicId: st.id,
            name: st.title,
            hasOverride: true,
            originalHasOverride: true,
            originalRetention: overrideRow.retentionTarget,
            originalMaxInterval: overrideRow.maximumIntervalDays ?? null,
            retention: overrideRow.retentionTarget,
            maxInterval: overrideRow.maximumIntervalDays ?? DEFAULT_INTERVAL
          });
        } else {
          drafts.push({
            subtopicId: st.id,
            name: st.title,
            hasOverride: false,
            originalHasOverride: false,
            originalRetention: null,
            originalMaxInterval: null,
            retention: subjectRetention,
            maxInterval: subjectMaxInterval
          });
        }
      }
      subtopics = drafts;
      loading = false;
    })();
  });

  function toggleOverride(subtopicId: string, next: boolean): void {
    subtopics = subtopics.map((s) => {
      if (s.subtopicId !== subtopicId) return s;
      return {
        ...s,
        hasOverride: next,
        retention: next ? subjectRetention : s.retention,
        maxInterval: next ? subjectMaxInterval : s.maxInterval
      };
    });
  }

  function updateSubtopicRetention(subtopicId: string, value: number): void {
    subtopics = subtopics.map((s) =>
      s.subtopicId === subtopicId ? { ...s, retention: value } : s
    );
  }

  function updateSubtopicInterval(subtopicId: string, value: number): void {
    subtopics = subtopics.map((s) =>
      s.subtopicId === subtopicId ? { ...s, maxInterval: value } : s
    );
  }

  async function save(): Promise<void> {
    if (busy || !dirty) return;
    busy = true;
    message = null;
    try {
      await upsertMemoryWeights({
        nodeId: subjectId,
        retentionTarget: subjectRetention,
        maximumIntervalDays: subjectMaxInterval
      });

      for (const s of subtopics) {
        if (s.hasOverride) {
          await upsertMemoryWeights({
            nodeId: s.subtopicId,
            retentionTarget: s.retention,
            maximumIntervalDays: s.maxInterval
          });
        } else if (s.originalHasOverride) {
          await deleteMemoryWeightsForNode(s.subtopicId);
        }
      }

      initialSubjectRetention = subjectRetention;
      initialSubjectMaxInterval = subjectMaxInterval;
      subtopics = subtopics.map((s) => ({
        ...s,
        originalHasOverride: s.hasOverride,
        originalRetention: s.hasOverride ? s.retention : null,
        originalMaxInterval: s.hasOverride ? s.maxInterval : null
      }));

      store.invalidateRetentionCache();
      message = t('subjectSettings.saved');
    } finally {
      busy = false;
    }
  }

  function formatInterval(days: number): string {
    return `${days}${t('subjectSettings.daysSuffix')}`;
  }

  let open = $state(true);
</script>

<Modal
  bind:open
  title={t('subjectSettings.title')}
  description={subjectName}
  size="md"
  onclose={onClose}
>
  {#if loading}
    <p class="loading">{t('subjectSettings.loading')}</p>
  {:else}
    <div class="body">
      <section class="field">
        <div class="field-head">
          <span class="label">{t('subjectSettings.retention')}</span>
          <span class="value">{retentionLabel}</span>
        </div>
        <input
          class="slider"
          type="range"
          min={MIN_RETENTION}
          max={MAX_RETENTION}
          step={STEP_RETENTION}
          bind:value={subjectRetention}
          aria-label={t('subjectSettings.retention')}
          aria-valuetext={retentionLabel}
        />
        <p class="hint">{t('subjectSettings.retentionHint')}</p>
        <div class="scale">
          <span>{Math.round(MIN_RETENTION * 100)}%</span>
          <span>{Math.round(MAX_RETENTION * 100)}%</span>
        </div>
      </section>

      <Button variant="plain" size="sm" onclick={() => (showAdvanced = !showAdvanced)}>
        <Icon name="chevron-right" size={14} class={showAdvanced ? 'chevron-icon rotated' : 'chevron-icon'} />
        {t('subjectSettings.advanced')}
      </Button>

      {#if showAdvanced}
        <section class="field">
          <div class="field-head">
            <span class="label">{t('subjectSettings.maximumInterval')}</span>
            <span class="value">{intervalLabel}</span>
          </div>
          <input
            class="slider"
            type="range"
            min={MIN_INTERVAL}
            max={MAX_INTERVAL}
            step={STEP_INTERVAL}
            bind:value={subjectMaxInterval}
            aria-label={t('subjectSettings.maximumInterval')}
            aria-valuetext={intervalLabel}
          />
          <p class="hint">{t('subjectSettings.maximumIntervalHint')}</p>
          <div class="scale">
            <span>{MIN_INTERVAL}{t('subjectSettings.daysSuffix')}</span>
            <span>{MAX_INTERVAL}{t('subjectSettings.daysSuffix')}</span>
          </div>
        </section>
      {/if}

      {#if subtopics.length > 0}
        <Button variant="plain" size="sm" onclick={() => (showSubtopics = !showSubtopics)}>
          <Icon name="chevron-right" size={14} class={showSubtopics ? 'chevron-icon rotated' : 'chevron-icon'} />
          {t('subjectSettings.subtopics')} ({subtopics.length})
        </Button>

        {#if showSubtopics}
          <div class="subtopics">
            {#each subtopics as st (st.subtopicId)}
              <div class="subtopic">
                <div class="subtopic-head">
                  <span class="subtopic-name">{st.name}</span>
                  <Checkbox
                    checked={st.hasOverride}
                    onchange={(v) => toggleOverride(st.subtopicId, v)}
                    label={st.hasOverride
                      ? t('subjectSettings.overrideActive')
                      : t('subjectSettings.overrideInactive')}
                  />
                </div>

                {#if st.hasOverride}
                  <div class="subtopic-row">
                    <span class="mini-label">{t('subjectSettings.retentionShort')}</span>
                    <input
                      class="slider"
                      type="range"
                      min={MIN_RETENTION}
                      max={MAX_RETENTION}
                      step={STEP_RETENTION}
                      value={st.retention}
                      oninput={(e) =>
                        updateSubtopicRetention(
                          st.subtopicId,
                          Number((e.currentTarget as HTMLInputElement).value)
                        )}
                      aria-label={`${t('subjectSettings.retentionShort')} · ${st.name}`}
                    />
                    <span class="mini-value">{Math.round(st.retention * 100)}%</span>
                  </div>
                  <div class="subtopic-row">
                    <span class="mini-label">{t('subjectSettings.intervalShort')}</span>
                    <input
                      class="slider"
                      type="range"
                      min={MIN_INTERVAL}
                      max={MAX_INTERVAL}
                      step={STEP_INTERVAL}
                      value={st.maxInterval}
                      oninput={(e) =>
                        updateSubtopicInterval(
                          st.subtopicId,
                          Number((e.currentTarget as HTMLInputElement).value)
                        )}
                      aria-label={`${t('subjectSettings.intervalShort')} · ${st.name}`}
                    />
                    <span class="mini-value">{formatInterval(st.maxInterval)}</span>
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      {/if}

      {#if message}
        <p class="info">{message}</p>
      {/if}
    </div>
  {/if}

  {#snippet footer()}
    <Button variant="outline" disabled={busy} onclick={onClose}>
      {t('subjectSettings.cancel')}
    </Button>
    <Button variant="filled" disabled={busy || !dirty} loading={busy} onclick={() => void save()}>
      {t('subjectSettings.save')}
    </Button>
  {/snippet}
</Modal>

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .loading {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    text-align: center;
    margin: 0;
    padding: var(--space-4);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .field-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .label {
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    letter-spacing: var(--text-label-track);
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .value {
    font-size: var(--text-mono-data);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-weight: var(--weight-semibold);
  }

  .slider {
    width: 100%;
    accent-color: var(--color-accent);
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    line-height: 1.5;
  }

  .scale {
    display: flex;
    justify-content: space-between;
    font-size: var(--text-caption);
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }

  .subtopics {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .subtopic {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-2) 0;
    border-bottom: var(--border-width) solid var(--color-border);
  }

  .subtopic:last-child {
    border-bottom: none;
  }

  .subtopic-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }

  .subtopic-name {
    font-size: var(--text-body);
    font-weight: var(--weight-medium);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1;
  }

  .subtopic-row {
    display: grid;
    grid-template-columns: 5rem 1fr 4rem;
    align-items: center;
    gap: var(--space-2);
  }

  .mini-label {
    font-size: var(--text-caption);
    color: var(--color-text-muted);
  }

  .mini-value {
    font-size: var(--text-caption);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  .info {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-success);
    font-family: var(--font-mono);
  }

  :global(.chevron-icon) {
    transition: transform var(--dur-fast) var(--ease-out);
  }

  :global(.chevron-icon.rotated) {
    transform: rotate(90deg);
  }
</style>